import { useEffect } from "react";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import { Reveal } from "@/components/Reveal";

const EASE = [0.16, 1, 0.3, 1];

const SECTIONS = [
  {
    number: "01",
    title: "What I Collect",
    paragraphs: [
      "When you submit the contact form, I collect your name, work email, area of interest, and message. When you subscribe for blueprint notifications, I collect your email address only.",
      "This site does not use analytics, advertising trackers, or cookies of any kind. There are no accounts, no passwords, and no behavioral profiling.",
    ],
  },
  {
    number: "02",
    title: "How Your Information Is Used",
    paragraphs: [
      "Your details are used for one purpose only: to respond to your enquiry, or to send you the blueprint updates you explicitly asked for. Your information is never sold, rented, or shared with third parties for marketing purposes.",
    ],
  },
  {
    number: "03",
    title: "Storage & Security",
    paragraphs: [
      "Enquiry and subscription records are stored in a secured database with access restricted to me personally. Form submissions are validated and rate-limited to protect against abuse, and stored enquiry records are accessible only through a restricted, token-protected channel.",
    ],
  },
  {
    number: "04",
    title: "Third-Party Services",
    paragraphs: [
      "Meeting scheduling is handled by Calendly, and transactional email is delivered through a managed email service. Outbound links — including LinkedIn, Microsoft Learn, and industry publishers — are governed by the privacy policies of those services. I encourage you to review the policy of any third-party service you choose to use.",
    ],
  },
  {
    number: "05",
    title: "Your Choices",
    paragraphs: [
      "You may request to view, correct, or permanently delete your information at any time by emailing info@teccexec.com. To stop blueprint notifications, simply reply \u201cstop\u201d to any email you receive.",
    ],
  },
  {
    number: "06",
    title: "Contact",
    paragraphs: [
      "Questions about this policy or your data: info@teccexec.com — Tech Executive Capability Center LLC.",
    ],
  },
];

export default function PrivacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="privacy-page" className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 px-6 lg:px-12 pt-32 lg:pt-40 pb-24">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
              TECC LLC — Effective August 2026
            </p>
            <h1
              data-testid="privacy-title"
              className="font-serif tracking-[-0.02em] leading-[1.1] text-4xl sm:text-5xl lg:text-6xl text-white mb-8"
            >
              Privacy <span className="italic text-[#E6E4D9]">Policy</span>
            </h1>
            <p className="font-serif italic text-xl sm:text-2xl text-white/60 leading-relaxed pb-10 border-b border-white/10">
              Short version: I collect only what you type into a form, use it
              only to respond to you, and never share it. The long version
              follows — written plainly, because privacy policies should be
              readable.
            </p>
          </motion.div>

          <div className="mt-6">
            {SECTIONS.map((section, i) => (
              <Reveal key={section.number} delay={i * 0.05} y={20}>
                <section
                  data-testid={`privacy-section-${section.number}`}
                  className="py-10 border-b border-white/10 last:border-0"
                >
                  <span className="font-mono text-xs text-[#E6E4D9] tracking-[0.3em] block mb-4">
                    {section.number}
                  </span>
                  <h2 className="font-serif text-2xl sm:text-3xl text-white tracking-tight mb-5">
                    {section.title}
                  </h2>
                  {section.paragraphs.map((p, j) => (
                    <p
                      key={j}
                      className="text-base sm:text-lg font-light leading-relaxed text-white/70 mb-5 last:mb-0"
                    >
                      {p}
                    </p>
                  ))}
                </section>
              </Reveal>
            ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-white/10 px-6 lg:px-12 py-8">
        <div className="max-w-3xl mx-auto">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
            © 2026 Tech Executive Capability Center LLC
          </p>
        </div>
      </footer>
    </div>
  );
}
